/**
 * viewPref — which of the four Practice Ops views to draw, and which Wall theme.
 *
 * WHY THIS EXISTS (2026-10-30, plan §6). Practice Ops is ONE board drawn four ways: `board` (desktop),
 * `wall` (a TV, no buttons), `operatory` (a room's iPad) and `phone`. Nothing is stored server-side: the
 * choice is per DEVICE, because the front-desk PC, the hallway TV and a dentist's phone are different
 * screens of the same office. So this file is three things:
 *   1. the rule that picks a view     (resolveView: ?view= → stored → the screen width)
 *   2. the rule that picks a theme    (wallTheme: ?theme= → stored → light)
 *   3. the Wall's colour tokens       (WALL_THEMES — every colour is defined twice, light and dark)
 *
 * STORAGE NEVER THROWS. localStorage can be missing (private window), full, or blocked, and a TV browser
 * may have no storage object at all. Every read/write below takes the storage OBJECT (so a test passes a
 * fake) and swallows the failure: a read gives null, a write gives false. `null` storage is fine.
 *
 * QUERY. `query` is the vue-router `$route.query` object ({ view: 'wall', theme: 'dark' }); a bare string
 * (the value already pulled out) is accepted too. A repeated param (an array) uses its first value. Values
 * are trimmed and lower-cased, so `?view=Wall` works; anything not in the allow-list is ignored, never an error.
 *
 * ⛔ Absent ⇒ the SAFE choice: the Wall theme defaults to LIGHT, and a screen with no usable width gets the
 * desktop board. Wall and Operatory are only ever CHOSEN (query or stored), never picked by width.
 *
 * WALL TOKENS. The dark set is the design's (page #0f1413, surface #171d1c, border #2a3331, ink #f2f5f4,
 * muted #9ca4a2 — Practice-Ops-overhaul brief). Both sets keep the two wait colours and the status dots at
 * WCAG AA (>= 3:1 for non-text UI) against the card, and chip text >= 4.5:1 against its chip — computed in
 * cypress/node-tests/practice-ops-wall-theme.test.js. Four values differ from the design's first draft
 * because the draft failed that bar (see the notes on the tokens). Static hex only: the operatory iPads run
 * old Safari, so no oklch / color-mix.
 *
 * ⚠️ Keep this file import-free. The node-tests execute this exact file.
 */

export const VIEWS = Object.freeze(['board', 'wall', 'operatory', 'phone']);
export const THEMES = Object.freeze(['light', 'dark']);

/** Below this width (CSS px) the default view is the phone one. */
export const PHONE_MAX_WIDTH = 700;

export const VIEW_STORAGE_KEY = 'teamio_practice_ops_view';
export const OP_STORAGE_KEY = 'teamio_practice_ops_op';
export const WALL_THEME_STORAGE_KEY = 'teamio_practice_ops_wall_theme';

function norm(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

/** One `?key=` value from a route query object (or a bare string), first of an array, else ''. */
function queryValue(query, key) {
  if (typeof query === 'string') return norm(query);
  if (!query || typeof query !== 'object') return '';
  const raw = Array.isArray(query[key]) ? query[key][0] : query[key];
  return norm(raw);
}

export function isValidView(value) {
  return VIEWS.indexOf(norm(value)) !== -1;
}

export function isValidTheme(value) {
  return THEMES.indexOf(norm(value)) !== -1;
}

/** The view a screen of this width gets when nothing else says: phone below 700px, else the board. */
export function defaultViewForWidth(width) {
  const w = typeof width === 'number' ? width : Number(width);
  if (!Number.isFinite(w) || w <= 0) return 'board';
  return w < PHONE_MAX_WIDTH ? 'phone' : 'board';
}

/**
 * @param {{query?: (object|string), stored?: string, width?: number}} input
 * @returns {'board'|'wall'|'operatory'|'phone'}  a valid `?view=` wins, then a valid stored value, then the width default
 */
export function resolveView(input) {
  const { query, stored, width } = input && typeof input === 'object' ? input : {};
  const fromQuery = queryValue(query, 'view');
  if (isValidView(fromQuery)) return fromQuery;
  const fromStore = norm(stored);
  if (isValidView(fromStore)) return fromStore;
  return defaultViewForWidth(width);
}

/**
 * @returns {'light'|'dark'}  a valid `?theme=` overrides, then a valid stored value; absent or garbage ⇒ 'light'
 */
export function wallTheme(stored, query) {
  const fromQuery = queryValue(query, 'theme');
  if (isValidTheme(fromQuery)) return fromQuery;
  const fromStore = norm(stored);
  if (isValidTheme(fromStore)) return fromStore;
  return 'light';
}

// ── Wall scale ───────────────────────────────────────────────────────────────────────────────────

/** The frame the Wall is designed (and laid out) in, CSS px. */
export const WALL_DESIGN_WIDTH = 1920;
export const WALL_DESIGN_HEIGHT = 1080;
export const WALL_MIN_ZOOM = 0.5;
export const WALL_MAX_ZOOM = 3;

/**
 * The CSS `zoom` that fits the 1920 x 1080 Wall frame into a window: the smaller of the two ratios, so the frame is never cut off
 * (a 1280 x 720 laptop → 2/3, a 4K TV → 2). Clamped to 0.5 .. 3; a window with no usable size → 1.
 */
export function wallZoom(width, height) {
  const w = typeof width === 'number' ? width : Number(width);
  const h = typeof height === 'number' ? height : Number(height);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return 1;
  const zoom = Math.min(w / WALL_DESIGN_WIDTH, h / WALL_DESIGN_HEIGHT);
  return Math.min(WALL_MAX_ZOOM, Math.max(WALL_MIN_ZOOM, zoom));
}

// ── storage (never throws) ───────────────────────────────────────────────────────────────────────

/** The stored string, or null when the storage is missing, empty for that key, or throws. */
export function readStored(storage, key) {
  try {
    if (!storage || typeof storage.getItem !== 'function') return null;
    const value = storage.getItem(key);
    return typeof value === 'string' ? value : null;
  } catch (e) {
    return null;
  }
}

/** true when written; false when the storage is missing or throws. */
export function writeStored(storage, key, value) {
  try {
    if (!storage || typeof storage.setItem !== 'function') return false;
    storage.setItem(key, String(value));
    return true;
  } catch (e) {
    return false;
  }
}

/** The stored view if it is one of the four, else null. */
export function readView(storage) {
  const v = norm(readStored(storage, VIEW_STORAGE_KEY));
  return isValidView(v) ? v : null;
}

/** Refuses (false) anything that is not one of the four views, so garbage never reaches storage. */
export function writeView(storage, view) {
  const v = norm(view);
  return isValidView(v) ? writeStored(storage, VIEW_STORAGE_KEY, v) : false;
}

/** The Operatory view's room: the stored OperatoryNum as a positive integer, or null. */
export function readOp(storage) {
  const raw = readStored(storage, OP_STORAGE_KEY);
  if (raw === null || !/^\d+$/.test(raw.trim())) return null;
  const n = Number(raw.trim());
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

/** Stores an OperatoryNum (positive integer, number or digit string); refuses anything else. */
export function writeOp(storage, opNum) {
  const s = typeof opNum === 'number' ? String(opNum) : (typeof opNum === 'string' ? opNum.trim() : '');
  if (!/^\d+$/.test(s)) return false;
  const n = Number(s);
  return Number.isSafeInteger(n) && n > 0 ? writeStored(storage, OP_STORAGE_KEY, String(n)) : false;
}

/** The stored Wall theme if it is 'light' or 'dark', else null (callers pass the raw value to wallTheme). */
export function readWallTheme(storage) {
  const v = norm(readStored(storage, WALL_THEME_STORAGE_KEY));
  return isValidTheme(v) ? v : null;
}

/** Refuses (false) anything but 'light' / 'dark'. */
export function writeWallTheme(storage, theme) {
  const v = norm(theme);
  return isValidTheme(v) ? writeStored(storage, WALL_THEME_STORAGE_KEY, v) : false;
}

// ── Wall colour tokens ──────────────────────────────────────────────────────────────────────────

/**
 * Every Wall colour, defined TWICE. OpsWall.vue and the card (`dark` prop) read ONE `theme` value and
 * pick a set; there is no colour in the Wall that is not a key here.
 *
 *   page / column / card .... the three surfaces, back to front
 *   text / textSub / textMuted ... ink, secondary, quiet (all >= 4.5:1 on the CARD)
 *   amberBorder / redBorder ... the whole-card threshold border (1.5px); >= 3:1 on the card
 *   dot*  ................... the status dots (Open Dental status; the real card uses the office's own
 *                               colour, these are the legend and the fallback); >= 3:1 on the card
 *   chip<Tone>Bg / chip<Tone>Text ... timer-chip tones, text >= 4.5:1 on its chip
 *   stripeFallback .......... the provider stripe when the office's colour is near-white
 *
 * Differences from the design's first draft, each forced by the AA check (the design file is untouched):
 *   light amberBorder       #f59e0b -> #cb8309   (2.15:1 on white; 3.09:1 now)
 *   dark  dotInTreatment    #0f7259 -> #15775e   (2.91:1 on #171d1c; 3.12:1 now)
 *   light textMuted         #717977 -> #707876   (4.46:1 on white; 4.53:1 now)
 *   light chipDismissedText #717977 -> #686f6d   (3.96:1 on its chip; 4.56:1 now)
 */
export const WALL_THEMES = Object.freeze({
  light: Object.freeze({
    pageBg: '#f7faf9',
    columnBg: '#eff3f2',
    cardBg: '#ffffff',
    border: '#dfe4e3',
    text: '#0c0f0e',
    textSub: '#3a4240',
    textMuted: '#707876',
    accent: '#0f7259',
    stripeFallback: '#c8cfcd',
    amberBorder: '#cb8309',
    redBorder: '#e5484d',
    dotArrived: '#1a73d5',
    dotReady: '#009360',
    dotInTreatment: '#0f7259',
    dotDismissed: '#787986',
    dotUnconfirmed: '#d06f00',
    chipNeutralBg: '#eef2f1',
    chipNeutralText: '#3a4240',
    chipAmberBg: '#fef6e7',
    chipAmberText: '#b45309',
    chipRedBg: '#fdecec',
    chipRedText: '#c2333a',
    chipSeatedBg: '#e9f6f1',
    chipSeatedText: '#0b5c49',
    chipDismissedBg: '#eef2f1',
    chipDismissedText: '#686f6d',
  }),
  dark: Object.freeze({
    pageBg: '#0f1413',
    columnBg: '#141a19',
    cardBg: '#171d1c',
    border: '#2a3331',
    text: '#f2f5f4',
    textSub: '#c9d0ce',
    textMuted: '#9ca4a2',
    accent: '#0f7259',
    stripeFallback: '#c8cfcd',
    amberBorder: '#f59e0b',
    redBorder: '#e5484d',
    dotArrived: '#1a73d5',
    dotReady: '#009360',
    dotInTreatment: '#15775e',
    dotDismissed: '#787986',
    dotUnconfirmed: '#d06f00',
    chipNeutralBg: '#222a28',
    chipNeutralText: '#c9d0ce',
    chipAmberBg: '#3a2c10',
    chipAmberText: '#fbbf24',
    chipRedBg: '#3b1a1c',
    chipRedText: '#ff8589',
    chipSeatedBg: '#123029',
    chipSeatedText: '#5fd0ad',
    chipDismissedBg: '#222a28',
    chipDismissedText: '#9ca4a2',
  }),
});
